"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, PiggyBank, Zap, Award, MessageSquareHeart as MessageSquareLike, Sparkles, ArrowRight, CircleCheck as CheckCircle2, CircleX as XCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const benefitsList = [{
  id: 1,
  title: "100% Direct Consultant Access",
  consultantText: "You work directly with me, Yassine. Every strategy, technical audit, and phone call is handled personally by an experienced practitioner.",
  agencyText: "Pitch by senior director, then passed off to junior account managers or entry-level executives.",
  icon: UserCheck,
  highlight: "No Middlemen"
}, {
  id: 2,
  title: "Cost Efficiency & Zero Overhead",
  consultantText: "Your budget goes straight into high-impact SEO work. You pay purely for senior expertise, not agency office space, account managers, or sales commissions.",
  agencyText: "High monthly retainers inflated to cover large agency overheads, account management fees, and non-essential staff.",
  icon: PiggyBank,
  highlight: "Maximum ROI"
}, {
  id: 3,
  title: "Agile, Tailored Execution",
  consultantText: "Custom SEO strategies built specifically for your business goals, implemented quickly without bureaucratic delays or rigid scope limits.",
  agencyText: "Standardized 'assembly line' packages and cookie-cutter deliverables that rarely adapt to real-time search updates.",
  icon: Zap,
  highlight: "Bespoke Strategy"
}, {
  id: 4,
  title: "Senior Hands-On Expertise",
  consultantText: "Deep technical, content, and strategic experience applied directly to your codebase and content architecture.",
  agencyText: "Inconsistent work quality dependent on whichever junior specialist happens to be assigned to your account.",
  icon: Award,
  highlight: "Proven Experience"
}, {
  id: 5,
  title: "Honest & Transparent Communication",
  consultantText: "Direct, plain-English advice and honest reporting focused on real revenue and organic growth—never vanity metrics or disguised fluff.",
  agencyText: "Generic automated monthly PDF reports filled with technical jargon designed to hide lack of actual traction.",
  icon: MessageSquareLike,
  highlight: "true Accountability"
}, {
  id: 6,
  title: "Modern AI & Search Innovation",
  consultantText: "Proactive, agile optimization for modern search engines and AI Search Overviews, tested and deployed without waiting for agency sign-off.",
  agencyText: "Slow to adapt to major search engine shifts, waiting for corporate approvals before modifying client strategies.",
  icon: Sparkles,
  highlight: "Future-Ready"
}];
export default function Consultantbenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      {}
      <div className="aria-hidden:hidden pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/60 blur-3xl" aria-hidden="true" />
      <div className="aria-hidden:hidden pointer-events-none absolute bottom-10 -left-20 size-80 rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-sm font-medium border border-accent/20">
            Why Choose Direct Consultancy
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default mb-6">
            Independent SEO Consultant vs Traditional Agency
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            When you hire an SEO agency, you often pay for account managers, sales teams, and corporate overhead. 
            When you hire me, you get a dedicated senior specialist who takes full accountability for your organic growth.
          </p>
        </div>

        {}
        <div className="mb-14 max-w-4xl mx-auto">
          <Card className="border-2 border-accent/30 bg-card shadow-md overflow-hidden rounded-2xl">
            <CardContent className="p-6 sm:p-8 flex flex-col md:flex-row items-center gap-6">
              <div className="size-20 shrink-0 rounded-2xl bg-secondary flex items-center justify-center text-primary border border-accent/20">
                <UserCheck className="size-10 text-primary" />
              </div>
              <div className="space-y-2 text-center md:text-left">
                <div className="inline-block bg-accent/15 text-foreground font-semibold px-3 py-1 rounded-md text-xs uppercase tracking-wider mb-1">
                  My Personal Commitment
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  {"\"When you hire YE Consultancy, you work directly with me—every single day.\""}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  I don{"'"}t delegate your account to junior staff or outsource technical audits. I personally design, implement, and refine your entire SEO strategy to deliver sustainable search performance and maximum ROI for your business.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefitsList.map((benefit, index) => {
          const Icon = benefit.icon;
          return <motion.div key={benefit.id} variants={itemVariants} data-index={index}>
                <Card className="h-full bg-card border border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] hover:border-accent/40 rounded-xl flex flex-col justify-between">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className="size-12 rounded-lg bg-secondary/80 flex items-center justify-center text-primary">
                        <Icon className="size-6" />
                      </div>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-secondary text-foreground border border-accent/20">
                        {benefit.highlight}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-foreground mb-4 font-default">
                      {benefit.title}
                    </h3>

                    {}
                    <div className="mb-4 p-3.5 rounded-lg bg-background border-l-4 border-primary/90 space-y-1">
                      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary">
                        <CheckCircle2 className="size-4 shrink-0 text-primary" />
                        <span>With Yassine (Freelance Specialist)</span>
                      </div>
                      <p className="text-sm text-foreground leading-relaxed">
                        {benefit.consultantText}
                      </p>
                    </div>

                    {}
                    <div className="mt-auto p-3.5 rounded-lg bg-muted/60 border-l-4 border-muted-foreground/30 space-y-1">
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <XCircle className="size-4 shrink-0 text-muted-foreground/70" />
                        <span>Traditional SEO Agency</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {benefit.agencyText}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {}
        <div className="mt-16 text-center bg-card border border-border rounded-2xl p-8 sm:p-10 shadow-sm max-w-4xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Ready to Skip the Agency Bloat?
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6 text-sm sm:text-base">
            Let{"'"}s discuss your search growth goals directly. Book a free, no-obligation consultation with me today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="w-full sm:w-auto font-semibold group" asChild>
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto" asChild>
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}